// Reading an Auth0 redirect that did not end in a usable session.
//
// Both apps hand the login dance to @auth0/auth0-spa-js and take `isAuthenticated()` for the
// answer to "is this user signed in". That answer comes from the CACHED USER ENTRY, which is a
// different localStorage key from the access/refresh token entry — so "signed in" and "able to call
// the API" can disagree, and when they do the app boots as a signed-in user whose very first
// request throws `missing_refresh_token` before anything renders (Sentry DASHBOARD-BW: five such
// boots across four users in two days, on prod and dev alike).
//
// Two shapes produce it, and the helpers here cover both. Either the redirect callback failed —
// the single-use `code` was spent, expired in a restored tab, or its POST to /oauth/token never got
// an answer — leaving the session unestablished while the previous user entry still sits in the
// cache; or the token entry simply aged out while the user entry did not. Everything here is a pure
// read of its arguments, so it stays import-time safe for this package's `sideEffects` list.

// Auth0 GenericError codes meaning the silent session is gone and only an interactive login can
// bring it back. Both apps carried this list separately, and the reason it belongs in one place is
// that it is not just a redirect trigger: it is also what tells an ordinary re-login apart from the
// app genuinely failing to start, and those two answers must not drift between the apps.
//
// `invalid_grant` is the one that comes back over the wire rather than from the SDK's own checks:
// a non-OK /oauth/token response is thrown as `GenericError(json.error, json.error_description)`,
// and on the refresh grant — the only grant `getTokenSilently` runs here, since the code exchange
// is answered separately by the callback handler — that is Auth0 saying the refresh token is
// expired, revoked, or a rotated one being reused. Nothing but an interactive login recovers it,
// and it is a NEIGHBOUR of `missing_refresh_token`, not an alternative: the SDK never removes a
// rejected refresh token from the cache, so the same dead session answers `invalid_grant` on every
// later attempt rather than degrading into a missing one.
export const REAUTH_ERRORS = [
  "login_required",
  "consent_required",
  "interaction_required",
  "missing_refresh_token",
  "invalid_grant",
];

export function isReauthError(error) {
  return REAUTH_ERRORS.includes(error?.error);
}

// Everything Auth0 appends to the return URL. `code`/`state` is a successful authorize; `error` and
// its friends are the same round trip refused (consent withdrawn, a rule that blocked the login),
// which arrives at exactly the same place and used to fall straight through to the guard — which
// sent the user back to Auth0 to be refused again.
const CALLBACK_PARAMS = ["code", "state", "error", "error_description", "error_uri"];

export function hasCallbackParams(search) {
  const params = new URLSearchParams(search);
  return (params.has("code") && params.has("state")) || params.has("error");
}

// The address to put back in the bar once a callback is done with, successfully or not. A spent
// `code` left in the URL is a trap: every reload of that page re-attempts the same dead exchange,
// so the failure repeats for as long as the user keeps trying the thing that usually fixes a broken
// page. Any query the app itself put there is preserved — a callback can land on a deep link.
export function urlWithoutCallbackParams(pathname, search) {
  const params = new URLSearchParams(search);
  CALLBACK_PARAMS.forEach((name) => params.delete(name));
  const rest = params.toString();
  return rest ? `${pathname}?${rest}` : pathname;
}

// One automatic re-login per tab, after which the user is told instead.
//
// The natural repair for a failed callback is to ask Auth0 again — the SSO session cookie is
// usually still good, so it costs a round trip nobody sees. The danger is that it is also a perfect
// loop: authorize returns instantly, the callback fails again, and the tab spins between the two
// forever with nothing on screen and a Sentry event per turn. So the retry is spent from session
// storage, and a second failure stops and surfaces. Storage being unreadable (private-mode quotas,
// a locked-down browser) answers "no retry" — refusing to loop is the safe side of that guess.
const RETRY_KEY = "auth.callback-retried";

export function takeCallbackRetry(storage) {
  if (!storage) return false;
  try {
    if (storage.getItem(RETRY_KEY)) return false;
    storage.setItem(RETRY_KEY, "1");
    return true;
  } catch {
    return false;
  }
}

export function clearCallbackRetry(storage) {
  try {
    storage?.removeItem(RETRY_KEY);
  } catch {
    // Nothing to do: an unwritable store cannot be holding a stale marker either.
  }
}
